import { useMemo, useCallback } from "react"
import { chain, filter } from "lodash"
import moment from "moment"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineGraph from "renderers/multi-line-graph"
import { createExposureMultiDateQuery, createExposureIntradayBatchTrendQueries } from "gatherers/multi-date/exposure"
import { combineResultsTolerantly, isIntradaySimulationEligible } from "gatherers/helpers"
import { filterItemsWithData, extractDatesFromFirstSeries, extractDatesFromEachSeries } from "connected-components/graph/helpers"
import { useBaseWithSegment, useFilteredSegment } from "connected-components/dashboard/helpers"
import { useThemeChartColors } from "hooks/use-theme-chart-colors"
import { getFactorLabel } from "helpers/factors"
import { numberFormatter } from "helpers/formatters/numbers"
import { ModelMetadata } from "resources/models"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import { useIntradayState, useIntradayBatchTrendMode, getIntradayChartMode } from "connected-components/dashboard/hooks/use-intraday-state"
import type { ClickPosition } from "connected-components/dashboard/drilldown/types"
import { useGraphTemplateChart } from "../../hooks/use-graph-template-chart"
import { GRAPH_EXPOSURE_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS } from "../template-chart-config"
import { useGraphData } from "../../hooks/use-graph-data"
import { useIntradayQueryInvalidation } from "connected-components/dashboard/hooks/use-intraday-query-invalidation"

interface ExposureItem {
  id: string
  label: string
  category: string
  disabled: boolean
  type: "factor"
}

interface ExposureDataPoint {
  date: moment.Moment
  [key: string]: unknown
}

function createExposureItems(
  modelMetadata: ModelMetadata,
  selectedCategory: string,
  disabledItems: Record<string, boolean | undefined>,
): ExposureItem[] {
  if (!modelMetadata?.factors) return []

  const factorsByIdMap = chain(modelMetadata.factors).keyBy("id").value()

  const factors = selectedCategory === "total"
    ? modelMetadata.factors
    : filter(modelMetadata.factors, factor => factor.category === selectedCategory)

  return factors.map(factor => ({
    id: factor.id,
    label: getFactorLabel(factorsByIdMap, factor.id),
    category: factor.category,
    disabled: Boolean(disabledItems[factor.id]),
    type: "factor" as const,
  }))
}

interface ExposureGraphProps {
  modelMetadata: ModelMetadata
}

export default function ExposureGraph({ modelMetadata }: ExposureGraphProps) {
  const {
    startDate,
    endDate,
    base,
    filter: portfolioFilter,
    normalization,
    instrument,
    comparisonInstrument,
    secondInstrument,
    isEndVsStartDateComparison,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    enableItems,
    intradayBatchMetadata,
    intradayPricingAsOf,
    isIntradayMode,
  } = useGraphData()

  const { selectedCategory } = useGraphTemplateChart(GRAPH_EXPOSURE_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS)
  const [{ lastIntradayBatchId }] = useIntradayState()
  const intradayBatchId = intradayBatchMetadata?.id

  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()

  const { isBatchTrendMode, completedBatches, isTruncated } = useIntradayBatchTrendMode(startDate, endDate, isIntradayMode, lastIntradayBatchId, intradayBatchId)

  const handleDotClick = useCallback((clickPosition: ClickPosition | undefined, focusedItemId: string | null | undefined) => {
    if (!panelId || !focusedItemId) return
    // A batch-trend point is stamped with its own batch's asOf, not the latest — resolve the
    // clicked point back to its batch so drilldown doesn't open with the wrong snapshot.
    const clickedBatch = isBatchTrendMode ? completedBatches.find(batch => batch.id === clickPosition?.batchId) : undefined
    // clickedBatch.date is authoritative; clickPosition.date can shift a day across timezones.
    const snapshotDate = clickedBatch?.date ?? clickPosition?.date ?? endDate
    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "graph.exposure",
      title: "Asset View: Exposure",
      view: "exposure",
      context: {
        modelId: modelMetadata.id,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base,
        startDate,
        endDate: snapshotDate,
        isIntradayMode,
        // A live-tick click has no clickedBatch; don't fall back to intradayBatchMetadata's latest batch.
        ...resolveIntradayDrilldownFields(isIntradayMode, isBatchTrendMode ? clickedBatch : intradayBatchMetadata, intradayPricingAsOf),
        filter: portfolioFilter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: {
        selectedFactor: focusedItemId,
        selectedCategory,
        clickedDate: snapshotDate,
      },
    }))
  }, [panelId, openDrilldown, selectedCategory, modelMetadata.id, instrument, comparisonInstrument, secondInstrument, base, startDate, endDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, portfolioFilter, normalization, isEndVsStartDateComparison, isBatchTrendMode, completedBatches])

  const baseWithSegment = useBaseWithSegment(base ?? null, portfolioFilter ?? undefined, normalization)
  const segment = useFilteredSegment(portfolioFilter ?? undefined, normalization)
  const instrumentWithSegment = useMemo(() => ({ ...instrument, segment }), [instrument, segment])

  const chartColors = useThemeChartColors()
  const currentItems = useMemo(
    () => createExposureItems(modelMetadata, selectedCategory, disabledItems),
    [modelMetadata, selectedCategory, disabledItems],
  )

  const filterItems = useCallback(
    (items: ExposureItem[], data: ExposureDataPoint[]) => filterItemsWithData(items, data, chartColors.series),
    [chartColors.series],
  )

  const gatherers = useMemo(() => {
    const liveQuery = createExposureMultiDateQuery(
      modelMetadata.id,
      instrumentWithSegment,
      baseWithSegment,
      startDate,
      endDate,
      isIntradayMode,
      { batchId: intradayBatchId, lastIntradayBatchId },
    )
    // Batches append to the live-tick series instead of replacing it.
    if (!isBatchTrendMode) return [liveQuery]
    return [liveQuery, ...createExposureIntradayBatchTrendQueries(modelMetadata.id, instrumentWithSegment, baseWithSegment, completedBatches)]
  }, [isBatchTrendMode, completedBatches, modelMetadata, instrumentWithSegment, baseWithSegment, startDate, endDate, isIntradayMode, intradayBatchId, lastIntradayBatchId])

  useIntradayQueryInvalidation(gatherers, intradayBatchMetadata, startDate, endDate, intradayPricingAsOf, lastIntradayBatchId)

  const intradayChartMode = getIntradayChartMode(isBatchTrendMode, isIntradaySimulationEligible(isIntradayMode, startDate, endDate, intradayBatchId, lastIntradayBatchId))
  const isOneDayMode = intradayChartMode !== "none"
  const noDataMessage = isOneDayMode ? "Intraday exposure data isn't available yet." : undefined

  return <DataIntegrator
    dataTransform={isBatchTrendMode ? extractDatesFromEachSeries : extractDatesFromFirstSeries}
    combine={isBatchTrendMode ? combineResultsTolerantly : undefined}
    metrics={gatherers}
    Renderer={MultiLineGraph}
    rendererProps={{
      items: currentItems,
      filterItems,
      endDate,
      startDate,
      setItems: setDisabledItems,
      setOnlyItem,
      enableItems,
      onDotClick: handleDotClick,
      formatter: numberFormatter,
      loadingMessage: "Loading exposure data...",
      errorMessage: "Failed to load exposure data. Please try again.",
      emptyMessage: "No exposure data available.",
      noDataMessage,
      isOneDayMode,
      intradayChartMode,
      isTruncated,
    }}
  />
}
